import React, { useState, useEffect, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DAY_COLORS = {
  Monday: '#667eea',
  Tuesday: '#f97316',
  Wednesday: '#22c55e',
  Thursday: '#a855f7',
  Friday: '#ef4444',
  Saturday: '#06b6d4',
  Sunday: '#64748b'
};

/**
 * ExerciseRow - Single exercise within a day
 */
const ExerciseRow = ({ exercise, index }) => (
  <div
    data-testid={`exercise-${index}`}
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: '10px',
      padding: '8px 0',
      borderBottom: '1px solid rgba(255,255,255,0.04)'
    }}
  >
    <span style={{
      width: '20px', height: '20px', borderRadius: '50%',
      background: 'rgba(102, 126, 234, 0.15)', color: '#667eea',
      fontSize: '10px', fontWeight: '700', display: 'flex',
      alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px'
    }}>
      {index + 1}
    </span>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', lineHeight: 1.3 }}>
        {exercise.name}
      </div>
      <div style={{ display: 'flex', gap: '12px', marginTop: '3px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '11px', color: '#667eea', fontWeight: '600' }}>
          {exercise.sets} x {exercise.reps}
        </span>
        {exercise.rest && (
          <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
            Rest: {exercise.rest}
          </span>
        )}
        {exercise.tempo && (
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            Tempo: {exercise.tempo}
          </span>
        )}
      </div>
      {exercise.notes && (
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '3px', fontStyle: 'italic' }}>
          {exercise.notes}
        </div>
      )}
    </div>
  </div>
);

/**
 * DayCard - One day of the workout plan
 */
const DayCard = ({ day, isExpanded, onToggle }) => {
  const color = DAY_COLORS[day.day] || '#667eea';
  const isRest = day.is_rest;

  return (
    <div
      data-testid={`workout-day-${day.day?.toLowerCase()}`}
      style={{
        background: isRest
          ? 'rgba(100, 116, 139, 0.06)'
          : 'rgba(255,255,255,0.03)',
        border: `1px solid ${isRest ? 'rgba(100,116,139,0.15)' : `${color}22`}`,
        borderRadius: '10px',
        marginBottom: '8px',
        overflow: 'hidden',
        transition: 'all 0.2s ease'
      }}
    >
      {/* Day Header */}
      <div
        onClick={onToggle}
        style={{
          padding: '10px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          borderLeft: `3px solid ${color}`
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '13px', fontWeight: '700', color }}>
            {day.day?.slice(0, 3)}
          </span>
          <span style={{ fontSize: '12px', fontWeight: '500', color: 'var(--text-primary)' }}>
            {day.label || (isRest ? 'Rest / Recovery' : 'Training Day')}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {!isRest && day.duration_min && (
            <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
              ~{day.duration_min}min
            </span>
          )}
          <span style={{ fontSize: '12px', color: 'var(--text-muted)', transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'none' }}>
            &#9662;
          </span>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div style={{ padding: '0 14px 12px 17px' }}>
          {/* Warmup */}
          {day.warmup && (
            <div style={{ padding: '6px 10px', background: 'rgba(34,197,94,0.06)', borderRadius: '6px', marginBottom: '8px' }}>
              <span style={{ fontSize: '10px', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase' }}>Warmup</span>
              <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>{day.warmup}</p>
            </div>
          )}

          {/* Exercises */}
          {(day.exercises || []).map((ex, i) => (
            <ExerciseRow key={i} exercise={ex} index={i} />
          ))}

          {/* Cooldown */}
          {day.cooldown && (
            <div style={{ padding: '6px 10px', background: 'rgba(6,182,212,0.06)', borderRadius: '6px', marginTop: '8px' }}>
              <span style={{ fontSize: '10px', fontWeight: '700', color: '#06b6d4', textTransform: 'uppercase' }}>Cooldown</span>
              <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>{day.cooldown}</p>
            </div>
          )}

          {/* Supplement Timing */}
          {day.supplement_timing && (
            <div style={{ padding: '6px 10px', background: 'rgba(168,85,247,0.06)', borderRadius: '6px', marginTop: '8px' }}>
              <span style={{ fontSize: '10px', fontWeight: '700', color: '#a855f7', textTransform: 'uppercase' }}>Supplement Timing</span>
              <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>{day.supplement_timing}</p>
            </div>
          )}

          {/* Rest day info */}
          {isRest && !day.exercises?.length && (
            <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              {day.warmup || day.cooldown || 'Active recovery: light walking, stretching, foam rolling'}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * SessionView - Single workout session display
 */
const SessionView = ({ session }) => {
  if (!session) return null;
  return (
    <div data-testid="workout-session" style={{ marginTop: '12px' }}>
      <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>
        {session.session_name || session.focus || 'Today\'s Workout'}
      </div>
      {session.warmup && (
        <div style={{ padding: '6px 10px', background: 'rgba(34,197,94,0.06)', borderRadius: '6px', marginBottom: '8px' }}>
          <span style={{ fontSize: '10px', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase' }}>Warmup</span>
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>{session.warmup}</p>
        </div>
      )}
      {(session.exercises || []).map((ex, i) => (
        <ExerciseRow key={i} exercise={ex} index={i} />
      ))}
      {session.cooldown && (
        <div style={{ padding: '6px 10px', background: 'rgba(6,182,212,0.06)', borderRadius: '6px', marginTop: '8px' }}>
          <span style={{ fontSize: '10px', fontWeight: '700', color: '#06b6d4', textTransform: 'uppercase' }}>Cooldown</span>
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>{session.cooldown}</p>
        </div>
      )}
      {session.supplement_timing && (
        <div style={{ padding: '6px 10px', background: 'rgba(168,85,247,0.06)', borderRadius: '6px', marginTop: '8px' }}>
          <span style={{ fontSize: '10px', fontWeight: '700', color: '#a855f7', textTransform: 'uppercase' }}>Supplement Timing</span>
          <p style={{ margin: '2px 0 0', fontSize: '11px', color: 'var(--text-secondary)' }}>{session.supplement_timing}</p>
        </div>
      )}
    </div>
  );
};

/**
 * CrossfitWodView - Display EMOM or AMRAP workout
 */
const CrossfitWodView = ({ wod }) => {
  if (!wod) return null;
  const isEmom = wod.format === 'EMOM';

  return (
    <div data-testid="crossfit-wod-display" style={{ marginTop: '4px' }}>
      {/* WOD Header */}
      <div style={{
        background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)',
        borderRadius: '10px', padding: '12px 14px', marginBottom: '10px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: '700', color: '#ef4444' }}>
            {wod.workout_name || `${wod.format} ${wod.total_duration_min} min`}
          </div>
          <span style={{
            padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '800',
            background: 'rgba(239,68,68,0.15)', color: '#ef4444', letterSpacing: '1px',
          }}>{wod.format}</span>
        </div>
        {wod.overview && (
          <p style={{ margin: '6px 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{wod.overview}</p>
        )}
      </div>

      {/* Warmup */}
      {wod.warmup && (
        <div style={{ padding: '8px 12px', background: 'rgba(34,197,94,0.06)', borderRadius: '8px', marginBottom: '10px' }}>
          <span style={{ fontSize: '10px', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase' }}>
            Warmup ({wod.warmup.duration_min || 5} min)
          </span>
          {(wod.warmup.movements || []).map((m, i) => (
            <div key={i} style={{ display: 'flex', gap: '8px', marginTop: '4px', fontSize: '11px' }}>
              <span style={{ color: '#22c55e', fontWeight: '600' }}>{m.reps_or_duration}</span>
              <span style={{ color: 'var(--text-secondary)' }}>{m.name}</span>
              {m.notes && <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>— {m.notes}</span>}
            </div>
          ))}
        </div>
      )}

      {/* EMOM Minutes */}
      {isEmom && wod.minutes && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '10px' }}>
          {wod.minutes.map((m, i) => (
            <div key={i} data-testid={`emom-minute-${m.minute}`} style={{
              display: 'flex', gap: '10px', padding: '8px 12px',
              background: i % 2 === 0 ? 'rgba(239,68,68,0.04)' : 'rgba(255,255,255,0.02)',
              borderRadius: '6px', borderLeft: '3px solid #ef4444',
            }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                background: 'rgba(239,68,68,0.12)', color: '#ef4444',
                fontSize: '11px', fontWeight: '800', display: 'flex',
                alignItems: 'center', justifyContent: 'center',
              }}>{m.minute}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{m.movement}</div>
                <div style={{ fontSize: '11px', color: '#ef4444', fontWeight: '600', marginTop: '2px' }}>RX: {m.rx}</div>
                <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '1px' }}>Scaled: {m.scaled}</div>
                {m.target_work_time && (
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '1px' }}>Target: {m.target_work_time}</div>
                )}
                {m.notes && (
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '2px' }}>{m.notes}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AMRAP Circuit */}
      {!isEmom && wod.circuit && (
        <div style={{ marginBottom: '10px' }}>
          <div style={{
            fontSize: '10px', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase',
            letterSpacing: '0.5px', marginBottom: '6px',
          }}>Circuit — Repeat for {wod.total_duration_min} minutes</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {wod.circuit.map((c, i) => (
              <div key={i} data-testid={`amrap-movement-${c.order}`} style={{
                display: 'flex', gap: '10px', padding: '8px 12px',
                background: 'rgba(239,68,68,0.04)', borderRadius: '6px',
                borderLeft: '3px solid #ef4444',
              }}>
                <div style={{
                  width: '24px', height: '24px', borderRadius: '6px', flexShrink: 0,
                  background: 'rgba(239,68,68,0.12)', color: '#ef4444',
                  fontSize: '12px', fontWeight: '800', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                }}>{c.order}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{c.movement}</div>
                  <div style={{ fontSize: '11px', color: '#ef4444', fontWeight: '600', marginTop: '2px' }}>RX: {c.rx}</div>
                  <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '1px' }}>Scaled: {c.scaled}</div>
                  {c.notes && (
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic', marginTop: '2px' }}>{c.notes}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {wod.target_rounds && (
            <div style={{
              marginTop: '8px', padding: '6px 10px', background: 'rgba(239,68,68,0.06)',
              borderRadius: '6px', fontSize: '11px', color: 'var(--text-secondary)',
            }}>
              <span style={{ fontWeight: '700', color: '#ef4444' }}>Target: </span>{wod.target_rounds}
            </div>
          )}
        </div>
      )}

      {/* Scoring */}
      {wod.scoring && (
        <div style={{
          padding: '8px 12px', background: 'rgba(251,191,36,0.06)',
          border: '1px solid rgba(251,191,36,0.15)', borderRadius: '8px', marginBottom: '8px',
        }}>
          <span style={{ fontSize: '10px', fontWeight: '700', color: '#fbbf24', textTransform: 'uppercase' }}>Scoring</span>
          <p style={{ margin: '3px 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{wod.scoring}</p>
        </div>
      )}

      {/* Stimulus */}
      {wod.intended_stimulus && (
        <div style={{
          padding: '8px 12px', background: 'rgba(168,85,247,0.06)',
          border: '1px solid rgba(168,85,247,0.15)', borderRadius: '8px', marginBottom: '8px',
        }}>
          <span style={{ fontSize: '10px', fontWeight: '700', color: '#a855f7', textTransform: 'uppercase' }}>Intended Stimulus</span>
          <p style={{ margin: '3px 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{wod.intended_stimulus}</p>
        </div>
      )}

      {/* Cooldown */}
      {wod.cooldown && (
        <div style={{
          padding: '8px 12px', background: 'rgba(6,182,212,0.06)',
          borderRadius: '8px', marginBottom: '8px',
        }}>
          <span style={{ fontSize: '10px', fontWeight: '700', color: '#06b6d4', textTransform: 'uppercase' }}>Cooldown</span>
          <p style={{ margin: '3px 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{wod.cooldown}</p>
        </div>
      )}

      {/* Notes */}
      {wod.notes?.length > 0 && (
        <div style={{ marginTop: '4px' }}>
          {wod.notes.map((note, i) => (
            <p key={i} style={{ margin: '3px 0', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.4 }}>{note}</p>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * HomeRehabExercise - Single exercise in a rehab workout
 */
const HomeRehabExercise = ({ exercise, index }) => {
  const [expanded, setExpanded] = React.useState(false);
  const equipColors = {
    bodyweight: '#22c55e', band: '#a855f7', 'bosu ball': '#f97316', 'foam roller': '#06b6d4',
    dumbbells: '#3b82f6', 'stability ball': '#eab308', 'light dumbbells': '#3b82f6',
    'resistance bands': '#a855f7',
  };
  const eqColor = equipColors[exercise.equipment?.toLowerCase()] || '#64748b';

  return (
    <div data-testid={`rehab-exercise-${index}`} style={{
      background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
      borderRadius: '10px', padding: '12px', marginBottom: '8px',
    }}>
      <div onClick={() => setExpanded(!expanded)} style={{ cursor: 'pointer' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <span style={{
            width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(34,197,94,0.12)',
            color: '#22c55e', fontSize: '10px', fontWeight: '700', display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px'
          }}>{index + 1}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{exercise.name}</div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap', alignItems: 'center' }}>
              <span style={{ padding: '2px 7px', borderRadius: '4px', fontSize: '9px', fontWeight: '700',
                background: `${eqColor}18`, color: eqColor, letterSpacing: '0.3px', textTransform: 'uppercase' }}>
                {exercise.equipment}
              </span>
              <span style={{ fontSize: '11px', color: '#22c55e', fontWeight: '600' }}>
                {exercise.sets} x {exercise.reps_or_duration}
              </span>
              {exercise.rest && <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Rest: {exercise.rest}</span>}
            </div>
            {exercise.target_area && (
              <div style={{ fontSize: '10px', color: 'var(--text-secondary)', marginTop: '3px' }}>
                Targets: {exercise.target_area}
              </div>
            )}
          </div>
          <span style={{ fontSize: '14px', color: 'var(--text-muted)', transition: 'transform 0.2s',
            transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>&#9662;</span>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: '10px', marginLeft: '32px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {exercise.instructions && (
            <div style={{ padding: '8px 10px', background: 'rgba(34,197,94,0.05)', borderRadius: '6px', borderLeft: '2px solid rgba(34,197,94,0.3)' }}>
              <div style={{ fontSize: '9px', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase', marginBottom: '3px', letterSpacing: '0.5px' }}>Instructions</div>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{exercise.instructions}</p>
            </div>
          )}
          {exercise.easier_modification && (
            <div style={{ padding: '8px 10px', background: 'rgba(59,130,246,0.05)', borderRadius: '6px', borderLeft: '2px solid rgba(59,130,246,0.3)' }}>
              <div style={{ fontSize: '9px', fontWeight: '700', color: '#3b82f6', textTransform: 'uppercase', marginBottom: '3px', letterSpacing: '0.5px' }}>Easier Option</div>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{exercise.easier_modification}</p>
            </div>
          )}
          {exercise.progression && (
            <div style={{ padding: '8px 10px', background: 'rgba(168,85,247,0.05)', borderRadius: '6px', borderLeft: '2px solid rgba(168,85,247,0.3)' }}>
              <div style={{ fontSize: '9px', fontWeight: '700', color: '#a855f7', textTransform: 'uppercase', marginBottom: '3px', letterSpacing: '0.5px' }}>Progression</div>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{exercise.progression}</p>
            </div>
          )}
          {exercise.safety && (
            <div style={{ padding: '8px 10px', background: 'rgba(239,68,68,0.05)', borderRadius: '6px', borderLeft: '2px solid rgba(239,68,68,0.3)' }}>
              <div style={{ fontSize: '9px', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase', marginBottom: '3px', letterSpacing: '0.5px' }}>Safety</div>
              <p style={{ margin: 0, fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{exercise.safety}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * HomeRehabView - Displays a generated home rehab workout
 */
const HomeRehabView = ({ workout }) => {
  if (!workout) return null;
  return (
    <div data-testid="home-rehab-display" style={{ marginTop: '10px' }}>
      {/* Header */}
      <div style={{ marginBottom: '14px' }}>
        <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--text-primary)' }}>{workout.session_name}</div>
        <div style={{ display: 'flex', gap: '8px', marginTop: '6px', flexWrap: 'wrap' }}>
          {workout.difficulty && (
            <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '700',
              background: workout.difficulty === 'gentle' ? 'rgba(34,197,94,0.1)' : workout.difficulty === 'moderate' ? 'rgba(234,179,8,0.1)' : 'rgba(168,85,247,0.1)',
              color: workout.difficulty === 'gentle' ? '#22c55e' : workout.difficulty === 'moderate' ? '#eab308' : '#a855f7',
              textTransform: 'uppercase', letterSpacing: '0.5px' }}>{workout.difficulty}</span>
          )}
          {workout.estimated_duration_min && (
            <span style={{ padding: '3px 8px', borderRadius: '6px', fontSize: '10px', fontWeight: '600',
              background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)' }}>
              ~{workout.estimated_duration_min} min
            </span>
          )}
        </div>
        {workout.overview && (
          <p style={{ margin: '8px 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{workout.overview}</p>
        )}
      </div>

      {/* Safety Note */}
      {workout.safety_note && (
        <div style={{ padding: '10px 12px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)',
          borderRadius: '8px', marginBottom: '14px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '14px', flexShrink: 0 }}>&#9888;</span>
          <p style={{ margin: 0, fontSize: '11px', color: '#ef4444', lineHeight: 1.5 }}>{workout.safety_note}</p>
        </div>
      )}

      {/* Warmup */}
      {workout.warmup && (
        <div style={{ marginBottom: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: '#f97316', textTransform: 'uppercase',
            letterSpacing: '0.5px', marginBottom: '6px' }}>Warmup ({workout.warmup.duration_min} min)</div>
          {workout.warmup.movements?.map((m, i) => (
            <div key={i} style={{ padding: '6px 10px', background: 'rgba(249,115,22,0.04)',
              borderRadius: '6px', marginBottom: '4px', fontSize: '11px' }}>
              <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{m.name}</span>
              <span style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>{m.duration}</span>
              {m.instructions && <p style={{ margin: '3px 0 0', fontSize: '10px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{m.instructions}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Exercises */}
      <div style={{ fontSize: '10px', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase',
        letterSpacing: '0.5px', marginBottom: '8px' }}>Exercises (tap to expand)</div>
      {workout.exercises?.map((ex, i) => (
        <HomeRehabExercise key={i} exercise={ex} index={i} />
      ))}

      {/* Cooldown */}
      {workout.cooldown && (
        <div style={{ marginTop: '14px' }}>
          <div style={{ fontSize: '10px', fontWeight: '700', color: '#06b6d4', textTransform: 'uppercase',
            letterSpacing: '0.5px', marginBottom: '6px' }}>Cooldown ({workout.cooldown.duration_min} min)</div>
          {workout.cooldown.movements?.map((m, i) => (
            <div key={i} style={{ padding: '6px 10px', background: 'rgba(6,182,212,0.04)',
              borderRadius: '6px', marginBottom: '4px', fontSize: '11px' }}>
              <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{m.name}</span>
              <span style={{ color: 'var(--text-muted)', marginLeft: '8px' }}>{m.duration}</span>
              {m.instructions && <p style={{ margin: '3px 0 0', fontSize: '10px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>{m.instructions}</p>}
            </div>
          ))}
        </div>
      )}

      {/* Recommendations */}
      <div style={{ marginTop: '14px', padding: '10px 12px', background: 'rgba(34,197,94,0.04)',
        border: '1px solid rgba(34,197,94,0.1)', borderRadius: '8px' }}>
        {workout.frequency_recommendation && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ fontSize: '10px', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase' }}>Frequency: </span>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{workout.frequency_recommendation}</span>
          </div>
        )}
        {workout.progression_plan && (
          <div style={{ marginBottom: '8px' }}>
            <span style={{ fontSize: '10px', fontWeight: '700', color: '#a855f7', textTransform: 'uppercase' }}>Progression: </span>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{workout.progression_plan}</span>
          </div>
        )}
        {workout.when_to_stop && (
          <div>
            <span style={{ fontSize: '10px', fontWeight: '700', color: '#ef4444', textTransform: 'uppercase' }}>Stop If: </span>
            <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{workout.when_to_stop}</span>
          </div>
        )}
      </div>

      {/* Notes */}
      {workout.notes?.length > 0 && (
        <div style={{ marginTop: '10px' }}>
          {workout.notes.map((note, i) => (
            <p key={i} style={{ margin: '3px 0', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.4 }}>{note}</p>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * WorkoutPlanner - Main component
 * Generates and displays personalized workout plans.
 */
const WorkoutPlanner = ({ userId, onAskCoach, onWorkoutLogged }) => {
  const [plan, setPlan] = useState(null);
  const [session, setSession] = useState(null);
  const [crossfitWod, setCrossfitWod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSession, setLoadingSession] = useState(false);
  const [loadingCrossfit, setLoadingCrossfit] = useState(false);
  const [error, setError] = useState(null);
  const [expandedDays, setExpandedDays] = useState({});
  const [activeTab, setActiveTab] = useState('weekly'); // 'weekly' | 'session' | 'crossfit'
  const [sessionFocus, setSessionFocus] = useState('');
  const [planLoaded, setPlanLoaded] = useState(false);
  const [crossfitType, setCrossfitType] = useState('amrap'); // 'emom' | 'amrap'
  const [crossfitDuration, setCrossfitDuration] = useState(20); // 15, 20, 25
  const [savedWods, setSavedWods] = useState([]);
  const [savingWod, setSavingWod] = useState(false);
  const [showScoreForm, setShowScoreForm] = useState(null); // wod_id or null
  const [scoreInput, setScoreInput] = useState({ rounds: '', extra_reps: '', notes: '', rx_or_scaled: 'scaled' });
  const [loggingScore, setLoggingScore] = useState(false);
  const [wodSaved, setWodSaved] = useState(false);
  const [homeRehabWorkout, setHomeRehabWorkout] = useState(null);
  const [loadingHomeRehab, setLoadingHomeRehab] = useState(false);
  const [selectedEquipment, setSelectedEquipment] = useState(['bodyweight']);
  const [rehabFocus, setRehabFocus] = useState('full_body_strength');

  // Sport-specific state
  const [sportWorkout, setSportWorkout] = useState(null);
  const [loadingSport, setLoadingSport] = useState(false);
  const [selectedSport, setSelectedSport] = useState('hockey');
  const [selectedAgeGroup, setSelectedAgeGroup] = useState('adult_rec');
  const [selectedSeasonPhase, setSelectedSeasonPhase] = useState('off_season');

  // Workout logging state
  const [showLogForm, setShowLogForm] = useState(null); // { type, name, exercises, duration }
  const [logExercises, setLogExercises] = useState([]);
  const [logDuration, setLogDuration] = useState(30);
  const [logNotes, setLogNotes] = useState('');
  const [loggingWorkout, setLoggingWorkout] = useState(false);
  const [workoutLogs, setWorkoutLogs] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  // Load saved plan on first render
  const loadSavedPlan = useCallback(async () => {
    if (planLoaded) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/workout/plan/${userId}`);
      const data = await res.json();
      if (data.plan) {
        setPlan(data.plan);
        // Auto-expand today's day
        const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
        setExpandedDays({ [today]: true });
      }
      setPlanLoaded(true);
    } catch (e) {
      console.error('Failed to load workout plan:', e);
      setPlanLoaded(true);
    }
  }, [userId, planLoaded]);

  // Load on mount
  React.useEffect(() => { loadSavedPlan(); }, [loadSavedPlan]);

  const generatePlan = useCallback(async (type = 'weekly', focus = null, cfType = null, cfDuration = null, equipment = null, rehabFocusArg = null, sportArg = null, ageGroupArg = null, seasonPhaseArg = null) => {
    setWodSaved(false);
    const setLoad = type === 'weekly' ? setLoading : type === 'crossfit' ? setLoadingCrossfit : type === 'home_rehab' ? setLoadingHomeRehab : type === 'sport_specific' ? setLoadingSport : setLoadingSession;
    setLoad(true);
    setError(null);
    try {
      const body = {
        user_id: userId,
        plan_type: type,
        focus: focus || null
      };
      if (type === 'crossfit') {
        body.crossfit_type = cfType || 'amrap';
        body.duration_minutes = cfDuration || 20;
      }
      if (type === 'home_rehab') {
        body.equipment = equipment || ['bodyweight'];
        body.rehab_focus = rehabFocusArg || 'general_flexibility';
      }
      if (type === 'sport_specific') {
        body.sport = sportArg || 'hockey';
        body.age_group = ageGroupArg || 'adult_rec';
        body.season_phase = seasonPhaseArg || 'off_season';
      }
      const res = await fetch(`${BACKEND_URL}/api/workout/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: 'Failed to generate plan' }));
        throw new Error(err.detail || 'Failed to generate plan');
      }
      const data = await res.json();
      if (type === 'weekly') {
        setPlan(data.plan);
        const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
        setExpandedDays({ [today]: true });
      } else if (type === 'crossfit') {
        setCrossfitWod(data.plan);
      } else if (type === 'home_rehab') {
        setHomeRehabWorkout(data.plan);
      } else if (type === 'sport_specific') {
        setSportWorkout(data.plan);
      } else {
        setSession(data.plan);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoad(false);
    }
  }, [userId]);

  const toggleDay = (dayName) => {
    setExpandedDays(prev => ({ ...prev, [dayName]: !prev[dayName] }));
  };

  // Load saved WODs
  const loadSavedWods = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/workout/saved-wods/${userId}`);
      if (res.ok) {
        const data = await res.json();
        setSavedWods(data.wods || []);
      }
    } catch (e) {
      console.error('Failed to load saved WODs:', e);
    }
  }, [userId]);

  useEffect(() => { loadSavedWods(); }, [loadSavedWods]);

  const handleSaveWod = async () => {
    if (!crossfitWod) return;
    setSavingWod(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/workout/save-wod`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, wod_data: crossfitWod })
      });
      if (res.ok) {
        setWodSaved(true);
        loadSavedWods();
      }
    } catch (e) {
      console.error('Failed to save WOD:', e);
    }
    setSavingWod(false);
  };

  const handleLogScore = async (wodId) => {
    setLoggingScore(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/workout/log-score`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          wod_id: wodId,
          user_id: userId,
          rounds: parseInt(scoreInput.rounds) || 0,
          extra_reps: parseInt(scoreInput.extra_reps) || 0,
          notes: scoreInput.notes,
          rx_or_scaled: scoreInput.rx_or_scaled,
        })
      });
      if (res.ok) {
        setShowScoreForm(null);
        setScoreInput({ rounds: '', extra_reps: '', notes: '', rx_or_scaled: 'scaled' });
        loadSavedWods();
      }
    } catch (e) {
      console.error('Failed to log score:', e);
    }
    setLoggingScore(false);
  };

  const handleDeleteWod = async (wodId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/workout/saved-wods/${wodId}?user_id=${userId}`, {
        method: 'DELETE'
      });
      if (res.ok) loadSavedWods();
    } catch (e) {
      console.error('Failed to delete WOD:', e);
    }
  };

  // Extract exercises from any workout type for the log form
  const extractExercises = (workoutType, workoutData) => {
    let exercises = [];
    let name = '';
    let duration = 30;
    if (workoutType === 'weekly' && workoutData) {
      // For weekly, use the expanded day
      const dayKey = Object.keys(expandedDays).find(k => expandedDays[k]);
      const schedule = workoutData?.weekly_schedule || [];
      const day = schedule.find(d => d.day === dayKey) || schedule[0];
      if (day && !day.is_rest) {
        name = `${day.day} — ${day.label || 'Workout'}`;
        exercises = (day.exercises || []).map(e => ({
          name: e.name, completed: true,
          sets: e.sets || '', reps: e.reps || '', weight: '', notes: ''
        }));
        duration = day.duration_min || 45;
      }
    } else if (workoutType === 'session' && workoutData) {
      name = workoutData.session_name || 'Single Session';
      exercises = (workoutData.exercises || []).map(e => ({
        name: e.name || e.exercise, completed: true,
        sets: e.sets || '', reps: e.reps || '', weight: '', notes: ''
      }));
      duration = workoutData.estimated_duration_min || 45;
    } else if (workoutType === 'crossfit' && workoutData) {
      name = workoutData.wod_name || `${(workoutData.format || 'WOD').toUpperCase()} ${workoutData.total_duration_min || 20}min`;
      exercises = (workoutData.movements || []).map(m => ({
        name: m.name || m.movement, completed: true,
        sets: '', reps: m.reps || m.work || '', weight: m.rx || '', notes: ''
      }));
      duration = workoutData.total_duration_min || 20;
    } else if (workoutType === 'home_rehab' && workoutData) {
      name = workoutData.session_name || 'Home Rehab';
      exercises = (workoutData.exercises || []).map(e => ({
        name: e.name, completed: true,
        sets: e.sets || '', reps: e.reps_or_duration || '', weight: '', notes: ''
      }));
      duration = workoutData.estimated_duration_min || 25;
    }
    return { exercises, name, duration };
  };

  const openLogForm = (workoutType, workoutData) => {
    const { exercises, name, duration } = extractExercises(workoutType, workoutData);
    if (exercises.length === 0) return;
    setLogExercises(exercises);
    setLogDuration(duration);
    setLogNotes('');
    setShowLogForm({ type: workoutType, name });
  };

  const handleLogWorkout = async () => {
    if (!showLogForm) return;
    setLoggingWorkout(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/workout/log-workout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          workout_type: showLogForm.type,
          workout_name: showLogForm.name,
          duration_minutes: logDuration,
          exercises: logExercises,
          overall_notes: logNotes || null,
          tz_offset: -(new Date().getTimezoneOffset() / 60),
        })
      });
      if (res.ok) {
        const data = await res.json();
        setShowLogForm(null);
        loadWorkoutLogs();
        if (onWorkoutLogged) onWorkoutLogged();
        alert(`Workout logged! ${data.calories_burned} kcal burned`);
      }
    } catch (e) {
      console.error('Failed to log workout:', e);
    }
    setLoggingWorkout(false);
  };

  const loadWorkoutLogs = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/workout/logs/${userId}?limit=10`);
      const data = await res.json();
      if (data.success) setWorkoutLogs(data.logs || []);
    } catch (e) { console.error('Failed to load logs:', e); }
  }, [userId]);

  React.useEffect(() => { loadWorkoutLogs(); }, [loadWorkoutLogs]);

  const updateLogExercise = (idx, field, value) => {
    setLogExercises(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  const SESSION_FOCUSES = [
    { label: 'Full Body', value: 'full body workout' },
    { label: 'Upper Body', value: 'upper body (chest, back, shoulders, arms)' },
    { label: 'Lower Body', value: 'lower body (quads, hamstrings, glutes, calves)' },
    { label: 'Push', value: 'push day (chest, shoulders, triceps)' },
    { label: 'Pull', value: 'pull day (back, biceps, rear delts)' },
    { label: 'Core', value: 'core and ab workout' },
    { label: 'HIIT Cardio', value: 'HIIT cardio session for fat burning' },
    { label: 'Mobility', value: 'mobility and flexibility recovery session' },
  ];

  const EQUIPMENT_OPTIONS = [
    { label: 'Bodyweight', value: 'bodyweight' },
    { label: 'Pull-up/Chin-up Bar', value: 'pull-up bar' },
    { label: 'Push-up Bars', value: 'push-up bars' },
    { label: 'Resistance Bands', value: 'resistance bands' },
    { label: 'Dumbbells', value: 'dumbbells' },
    { label: 'Kettlebell', value: 'kettlebell' },
    { label: 'Jump Rope', value: 'jump rope' },
    { label: 'Ab Wheel', value: 'ab wheel' },
    { label: 'Yoga Mat', value: 'yoga mat' },
    { label: 'Foam Roller', value: 'foam roller' },
    { label: 'Stability Ball', value: 'stability ball' },
    { label: 'Bosu Ball', value: 'bosu ball' },
  ];

  const REHAB_FOCUSES = [
    { label: 'Full Body Strength', value: 'full_body_strength', color: '#667eea' },
    { label: 'Upper Body', value: 'upper_body', color: '#8b5cf6' },
    { label: 'Lower Body', value: 'lower_body', color: '#d946ef' },
    { label: 'Core & Abs', value: 'core', color: '#f43f5e' },
    { label: 'HIIT / Conditioning', value: 'hiit', color: '#ef4444' },
    { label: 'Lower Back', value: 'lower_back', color: '#f97316' },
    { label: 'Knee Recovery', value: 'knee', color: '#eab308' },
    { label: 'Shoulder Rehab', value: 'shoulder', color: '#22c55e' },
    { label: 'Hip Mobility', value: 'hip', color: '#06b6d4' },
    { label: 'Senior Mobility', value: 'senior_mobility', color: '#3b82f6' },
    { label: 'Balance & Falls', value: 'balance', color: '#0ea5e9' },
    { label: 'General Flexibility', value: 'general_flexibility', color: '#a855f7' },
    { label: 'Post-Surgery', value: 'post_surgery', color: '#ec4899' },
    { label: 'Neck & Upper Back', value: 'neck_upper_back', color: '#64748b' },
    { label: 'Ankle & Foot', value: 'ankle_foot', color: '#14b8a6' },
  ];

  const toggleEquipment = (val) => {
    setSelectedEquipment(prev =>
      prev.includes(val) ? prev.filter(e => e !== val) : [...prev, val]
    );
  };

  return (
    <div data-testid="workout-planner">
      {/* Tab Toggle */}
      <div style={{ display: 'flex', gap: '3px', marginBottom: '14px', background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '3px' }}>
        {['weekly', 'session', 'crossfit', 'sport', 'home'].map(tab => (
          <button
            key={tab}
            data-testid={`workout-tab-${tab}`}
            onClick={() => setActiveTab(tab === 'home' ? 'home_rehab' : tab === 'sport' ? 'sport_specific' : tab)}
            style={{
              flex: 1, padding: '8px 0', borderRadius: '6px', border: 'none',
              background: (tab === 'home' ? activeTab === 'home_rehab' : tab === 'sport' ? activeTab === 'sport_specific' : activeTab === tab)
                ? tab === 'crossfit' ? 'rgba(239,68,68,0.15)' : tab === 'home' ? 'rgba(34,197,94,0.15)' : tab === 'sport' ? 'rgba(251,191,36,0.15)' : 'rgba(102, 126, 234, 0.15)'
                : 'transparent',
              color: (tab === 'home' ? activeTab === 'home_rehab' : tab === 'sport' ? activeTab === 'sport_specific' : activeTab === tab)
                ? tab === 'crossfit' ? '#ef4444' : tab === 'home' ? '#22c55e' : tab === 'sport' ? '#fbbf24' : '#667eea'
                : 'var(--text-secondary)',
              fontSize: '10px', fontWeight: '600', cursor: 'pointer',
              transition: 'all 0.15s ease'
            }}
          >
            {tab === 'weekly' ? 'Weekly' : tab === 'session' ? 'Session' : tab === 'crossfit' ? 'CrossFit' : tab === 'sport' ? 'Sport' : 'Home'}
          </button>
        ))}
      </div>

      {error && (
        <div data-testid="workout-error" style={{
          padding: '10px 14px', background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.2)', borderRadius: '8px',
          fontSize: '12px', color: '#ef4444', marginBottom: '12px'
        }}>
          {error}
        </div>
      )}

      {/* WEEKLY TAB */}
      {activeTab === 'weekly' && (
        <>
          {!plan ? (
            <div data-testid="workout-empty" style={{ textAlign: 'center', padding: '20px 0' }}>
              <div style={{ fontSize: '32px', marginBottom: '12px' }}>&#127947;</div>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '0 0 6px', fontWeight: '600' }}>
                No workout plan yet
              </p>
              <p style={{ fontSize: '11px', color: 'var(--text-muted)', margin: '0 0 16px' }}>
                Generate a personalized weekly program based on your goals, health profile, and supplement stack
              </p>
              <button
                data-testid="generate-weekly-plan-btn"
                onClick={() => generatePlan('weekly')}
                disabled={loading}
                style={{
                  padding: '10px 24px', borderRadius: '8px', border: 'none',
                  background: loading ? 'rgba(102,126,234,0.3)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white', fontSize: '13px', fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                {loading ? 'Generating...' : 'Generate My Workout Plan'}
              </button>
            </div>
          ) : (
            <div data-testid="workout-plan-display">
              {/* Plan Header */}
              <div style={{ marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>
                    {plan.plan_name || 'Your Workout Plan'}
                  </div>
                  <button
                    data-testid="regenerate-plan-btn"
                    onClick={() => generatePlan('weekly')}
                    disabled={loading}
                    style={{
                      padding: '4px 10px', borderRadius: '6px', border: '1px solid rgba(102,126,234,0.3)',
                      background: 'transparent', color: '#667eea', fontSize: '10px', fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {loading ? '...' : 'Regenerate'}
                  </button>
                </div>
                {plan.overview && (
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {plan.overview}
                  </p>
                )}
              </div>

              {/* Day Cards */}
              {(plan.weekly_schedule || []).map((day, i) => (
                <DayCard
                  key={i}
                  day={day}
                  isExpanded={!!expandedDays[day.day]}
                  onToggle={() => toggleDay(day.day)}
                />
              ))}

              {/* Timeline */}
              {plan.timeline && (
                <div style={{
                  padding: '10px 14px', background: 'rgba(102,126,234,0.06)',
                  border: '1px solid rgba(102,126,234,0.15)', borderRadius: '8px', marginTop: '8px'
                }}>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: '#667eea', textTransform: 'uppercase' }}>
                    Progression Timeline
                  </span>
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {plan.timeline}
                  </p>
                </div>
              )}

              {/* Progression */}
              {plan.progression && (
                <div style={{
                  padding: '10px 14px', background: 'rgba(34,197,94,0.06)',
                  border: '1px solid rgba(34,197,94,0.15)', borderRadius: '8px', marginTop: '8px'
                }}>
                  <span style={{ fontSize: '10px', fontWeight: '700', color: '#22c55e', textTransform: 'uppercase' }}>
                    How to Progress
                  </span>
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                    {plan.progression}
                  </p>
                </div>
              )}

              {/* Notes */}
              {plan.notes?.length > 0 && (
                <div style={{ marginTop: '8px' }}>
                  {plan.notes.map((note, i) => (
                    <p key={i} style={{ margin: '4px 0', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                      {note}
                    </p>
                  ))}
                </div>
              )}

              {/* Ask Coach */}
              {onAskCoach && (
                <button
                  data-testid="workout-ask-coach-btn"
                  onClick={() => onAskCoach('Can you help me adjust my workout plan? I want to discuss modifications based on my progress and how I feel.')}
                  style={{
                    width: '100%', padding: '10px', borderRadius: '8px', marginTop: '12px',
                    border: '1px solid rgba(102,126,234,0.3)', background: 'rgba(102,126,234,0.06)',
                    color: '#667eea', fontSize: '12px', fontWeight: '600', cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                >
                  Discuss plan with Smart Coach
                </button>
              )}
              {/* Log Workout Button */}
              <button data-testid="log-weekly-workout-btn" onClick={() => openLogForm('weekly', plan)}
                style={{ width: '100%', padding: '10px', borderRadius: '8px', marginTop: '8px',
                  border: '1px solid rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.06)',
                  color: '#22c55e', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
                Log This Workout
              </button>
            </div>
          )}
        </>
      )}

      {/* SESSION TAB */}
      {activeTab === 'session' && (
        <div data-testid="workout-session-tab">
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 10px' }}>
            Generate a single workout tailored to today
          </p>

          {/* Focus chips */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px' }}>
            {SESSION_FOCUSES.map(f => (
              <button
                key={f.value}
                data-testid={`session-focus-${f.label.toLowerCase().replace(/\s/g, '-')}`}
                onClick={() => setSessionFocus(f.value)}
                style={{
                  padding: '6px 12px', borderRadius: '16px',
                  border: sessionFocus === f.value ? '1px solid #667eea' : '1px solid rgba(255,255,255,0.1)',
                  background: sessionFocus === f.value ? 'rgba(102,126,234,0.15)' : 'rgba(255,255,255,0.04)',
                  color: sessionFocus === f.value ? '#667eea' : 'var(--text-secondary)',
                  fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
              >
                {f.label}
              </button>
            ))}
          </div>

          <button
            data-testid="generate-session-btn"
            onClick={() => generatePlan('session', sessionFocus || 'a balanced workout for today')}
            disabled={loadingSession}
            style={{
              width: '100%', padding: '10px', borderRadius: '8px', border: 'none',
              background: loadingSession ? 'rgba(102,126,234,0.3)' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white', fontSize: '13px', fontWeight: '600',
              cursor: loadingSession ? 'not-allowed' : 'pointer', marginBottom: '12px'
            }}
          >
            {loadingSession ? 'Generating...' : "Generate Today's Session"}
          </button>

          <SessionView session={session} />
          {session && (
            <button data-testid="log-session-workout-btn" onClick={() => openLogForm('session', session)}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', marginTop: '10px',
                border: '1px solid rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.06)',
                color: '#22c55e', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
              Log This Workout
            </button>
          )}
        </div>
      )}

      {/* CROSSFIT TAB */}
      {activeTab === 'crossfit' && (
        <div data-testid="workout-crossfit-tab">
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '0 0 12px' }}>
            Generate a CrossFit-style WOD personalized to your profile
          </p>

          {/* EMOM / AMRAP Toggle */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
            {[
              { key: 'emom', label: 'EMOM', desc: 'Every Minute On the Minute' },
              { key: 'amrap', label: 'AMRAP', desc: 'As Many Rounds As Possible' },
            ].map(t => (
              <button
                key={t.key}
                data-testid={`crossfit-type-${t.key}`}
                onClick={() => setCrossfitType(t.key)}
                style={{
                  flex: 1, padding: '10px 8px', borderRadius: '10px',
                  border: crossfitType === t.key ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.1)',
                  background: crossfitType === t.key ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.03)',
                  cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s ease',
                }}
              >
                <div style={{
                  fontSize: '15px', fontWeight: '800', letterSpacing: '1px',
                  color: crossfitType === t.key ? '#ef4444' : 'var(--text-secondary)',
                }}>{t.label}</div>
                <div style={{ fontSize: '9px', color: 'var(--text-muted)', marginTop: '2px' }}>{t.desc}</div>
              </button>
            ))}
          </div>

          {/* Duration selector */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Session Time
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {[15, 20, 25].map(d => (
                <button
                  key={d}
                  data-testid={`crossfit-duration-${d}`}
                  onClick={() => setCrossfitDuration(d)}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: '8px',
                    border: crossfitDuration === d ? '1px solid #ef4444' : '1px solid rgba(255,255,255,0.1)',
                    background: crossfitDuration === d ? 'rgba(239,68,68,0.12)' : 'rgba(255,255,255,0.03)',
                    color: crossfitDuration === d ? '#ef4444' : 'var(--text-secondary)',
                    fontSize: '14px', fontWeight: '700', cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {d}<span style={{ fontSize: '10px', fontWeight: '500' }}> min</span>
                </button>
              ))}
            </div>
          </div>

          {/* Generate button */}
          <button
            data-testid="generate-crossfit-btn"
            onClick={() => generatePlan('crossfit', null, crossfitType, crossfitDuration)}
            disabled={loadingCrossfit}
            style={{
              width: '100%', padding: '12px', borderRadius: '8px', border: 'none',
              background: loadingCrossfit ? 'rgba(239,68,68,0.3)' : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              color: 'white', fontSize: '13px', fontWeight: '700',
              cursor: loadingCrossfit ? 'not-allowed' : 'pointer', marginBottom: '14px',
              letterSpacing: '0.5px',
            }}
          >
            {loadingCrossfit ? 'Programming WOD...' : `Generate ${crossfitType.toUpperCase()} ${crossfitDuration} min`}
          </button>

          {/* CrossFit WOD Display */}
          {crossfitWod && <CrossfitWodView wod={crossfitWod} />}

          {/* Save WOD Button */}
          {crossfitWod && (
            <button
              data-testid="save-wod-btn"
              onClick={handleSaveWod}
              disabled={savingWod || wodSaved}
              style={{
                width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid rgba(34,197,94,0.3)',
                background: wodSaved ? 'rgba(34,197,94,0.12)' : 'rgba(34,197,94,0.08)',
                color: wodSaved ? '#22c55e' : '#22c55e',
                fontSize: '13px', fontWeight: '600', cursor: wodSaved ? 'default' : 'pointer',
                marginTop: '10px', transition: 'all 0.2s',
              }}
            >
              {savingWod ? 'Saving...' : wodSaved ? 'WOD Saved' : 'Save This WOD'}
            </button>
          )}

          {/* Log CrossFit Workout Button */}
          {crossfitWod && (
            <button data-testid="log-crossfit-workout-btn" onClick={() => openLogForm('crossfit', crossfitWod)}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', marginTop: '6px',
                border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.06)',
                color: '#ef4444', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
              Log This Workout
            </button>
          )}

          {/* Saved WODs Section */}
          {savedWods.length > 0 && (
            <div data-testid="saved-wods-section" style={{ marginTop: '20px' }}>
              <div style={{
                fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)',
                textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px',
              }}>Saved WODs ({savedWods.length})</div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {savedWods.map(wod => (
                  <div key={wod.wod_id} data-testid={`saved-wod-${wod.wod_id}`} style={{
                    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
                    borderRadius: '10px', padding: '10px 12px',
                  }}>
                    {/* WOD Header */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>
                          {wod.workout_name}
                        </span>
                        <span style={{
                          marginLeft: '6px', padding: '2px 6px', borderRadius: '4px', fontSize: '9px',
                          fontWeight: '800', letterSpacing: '0.5px',
                          background: 'rgba(239,68,68,0.12)', color: '#ef4444',
                        }}>{wod.format} {wod.total_duration_min}m</span>
                      </div>
                      <button
                        data-testid={`delete-wod-${wod.wod_id}`}
                        onClick={() => handleDeleteWod(wod.wod_id)}
                        style={{
                          border: 'none', background: 'none', color: 'rgba(255,255,255,0.2)',
                          fontSize: '14px', cursor: 'pointer', padding: '2px 6px',
                        }}
                      >x</button>
                    </div>

                    {/* Score History */}
                    {wod.score_history?.length > 0 && (
                      <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                        {wod.score_history.slice(0, 5).map((s, i) => (
                          <div key={s.score_id || i} style={{
                            padding: '3px 8px', borderRadius: '6px', fontSize: '10px',
                            background: s.rx_or_scaled === 'rx' ? 'rgba(239,68,68,0.1)' : 'rgba(59,130,246,0.1)',
                            color: s.rx_or_scaled === 'rx' ? '#ef4444' : '#3b82f6',
                            fontWeight: '600',
                          }}>
                            {s.rounds}r{s.extra_reps > 0 ? `+${s.extra_reps}` : ''}
                            <span style={{ fontWeight: '400', opacity: 0.6, marginLeft: '3px' }}>
                              {s.rx_or_scaled === 'rx' ? 'RX' : 'SC'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Log Score Toggle */}
                    {showScoreForm === wod.wod_id ? (
                      <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <input
                            data-testid="score-rounds-input"
                            type="number"
                            placeholder="Rounds"
                            value={scoreInput.rounds}
                            onChange={e => setScoreInput(p => ({...p, rounds: e.target.value}))}
                            style={{
                              flex: 1, padding: '7px 10px', borderRadius: '6px',
                              border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                              color: '#fff', fontSize: '12px', outline: 'none',
                            }}
                          />
                          <input
                            data-testid="score-reps-input"
                            type="number"
                            placeholder="+Reps"
                            value={scoreInput.extra_reps}
                            onChange={e => setScoreInput(p => ({...p, extra_reps: e.target.value}))}
                            style={{
                              width: '70px', padding: '7px 10px', borderRadius: '6px',
                              border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                              color: '#fff', fontSize: '12px', outline: 'none',
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          {['scaled', 'rx'].map(opt => (
                            <button
                              key={opt}
                              data-testid={`score-${opt}-btn`}
                              onClick={() => setScoreInput(p => ({...p, rx_or_scaled: opt}))}
                              style={{
                                flex: 1, padding: '6px', borderRadius: '6px', border: 'none',
                                fontSize: '11px', fontWeight: '700', cursor: 'pointer',
                                textTransform: 'uppercase', letterSpacing: '0.5px',
                                background: scoreInput.rx_or_scaled === opt
                                  ? (opt === 'rx' ? 'rgba(239,68,68,0.15)' : 'rgba(59,130,246,0.15)')
                                  : 'rgba(255,255,255,0.04)',
                                color: scoreInput.rx_or_scaled === opt
                                  ? (opt === 'rx' ? '#ef4444' : '#3b82f6')
                                  : 'var(--text-muted)',
                              }}
                            >{opt}</button>
                          ))}
                        </div>
                        <input
                          data-testid="score-notes-input"
                          type="text"
                          placeholder="Notes (optional)"
                          value={scoreInput.notes}
                          onChange={e => setScoreInput(p => ({...p, notes: e.target.value}))}
                          style={{
                            padding: '7px 10px', borderRadius: '6px',
                            border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                            color: '#fff', fontSize: '11px', outline: 'none',
                          }}
                        />
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            data-testid="score-submit-btn"
                            onClick={() => handleLogScore(wod.wod_id)}
                            disabled={loggingScore || !scoreInput.rounds}
                            style={{
                              flex: 1, padding: '8px', borderRadius: '6px', border: 'none',
                              background: '#22c55e', color: '#fff', fontSize: '12px', fontWeight: '600',
                              cursor: loggingScore ? 'default' : 'pointer',
                              opacity: loggingScore || !scoreInput.rounds ? 0.5 : 1,
                            }}
                          >{loggingScore ? 'Saving...' : 'Log Score'}</button>
                          <button
                            onClick={() => setShowScoreForm(null)}
                            style={{
                              padding: '8px 14px', borderRadius: '6px', border: 'none',
                              background: 'rgba(255,255,255,0.06)', color: 'var(--text-secondary)',
                              fontSize: '12px', cursor: 'pointer',
                            }}
                          >Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <button
                        data-testid={`log-score-btn-${wod.wod_id}`}
                        onClick={() => {
                          setShowScoreForm(wod.wod_id);
                          setScoreInput({ rounds: '', extra_reps: '', notes: '', rx_or_scaled: 'scaled' });
                        }}
                        style={{
                          marginTop: '6px', padding: '6px 12px', borderRadius: '6px',
                          border: '1px solid rgba(34,197,94,0.2)', background: 'rgba(34,197,94,0.06)',
                          color: '#22c55e', fontSize: '11px', fontWeight: '600', cursor: 'pointer',
                        }}
                      >+ Log Score</button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* SPORT-SPECIFIC TAB */}
      {activeTab === 'sport_specific' && (
        <div data-testid="sport-specific-tab">
          <div style={{ marginBottom: '14px', padding: '10px 12px', background: 'rgba(251,191,36,0.04)',
            border: '1px solid rgba(251,191,36,0.1)', borderRadius: '8px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#fbbf24', marginBottom: '3px' }}>
              Sport-Specific Training
            </div>
            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
              AI-generated programs tailored to your sport, age group, and season phase.
            </div>
          </div>

          {/* Sport Selector */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Sport</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {[
                { id: 'hockey', label: 'Hockey' },
                { id: 'basketball', label: 'Basketball' },
                { id: 'soccer', label: 'Soccer' },
                { id: 'baseball', label: 'Baseball' },
                { id: 'football', label: 'Football' },
                { id: 'tennis', label: 'Tennis' },
                { id: 'swimming', label: 'Swimming' },
                { id: 'combat_sports', label: 'Combat Sports', sub: 'Boxing, BJJ, Wrestling, Judo, MMA' },
              ].map(s => (
                <button
                  key={s.id}
                  data-testid={`sport-${s.id}`}
                  onClick={() => setSelectedSport(s.id)}
                  style={{
                    padding: s.sub ? '6px 10px' : '6px 12px',
                    borderRadius: '16px',
                    border: selectedSport === s.id ? '1px solid #fbbf24' : '1px solid rgba(255,255,255,0.1)',
                    background: selectedSport === s.id ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.03)',
                    color: selectedSport === s.id ? '#fbbf24' : 'var(--text-secondary)',
                    fontSize: '11px', fontWeight: '500', cursor: 'pointer'
                  }}
                >
                  {s.label}
                  {s.sub && selectedSport === s.id && (
                    <span style={{ display: 'block', fontSize: '9px', color: 'rgba(251,191,36,0.7)', marginTop: '2px' }}>{s.sub}</span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Age Group */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Athlete Level</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {[
                { id: 'youth', label: 'Youth (8-12)' },
                { id: 'teen', label: 'Teen (13-17)' },
                { id: 'adult_rec', label: 'Adult Rec' },
                { id: 'competitive', label: 'Competitive' },
                { id: 'pro', label: 'Pro/Elite' },
              ].map(a => (
                <button
                  key={a.id}
                  data-testid={`age-${a.id}`}
                  onClick={() => setSelectedAgeGroup(a.id)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '16px',
                    border: selectedAgeGroup === a.id ? '1px solid #fbbf24' : '1px solid rgba(255,255,255,0.1)',
                    background: selectedAgeGroup === a.id ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.03)',
                    color: selectedAgeGroup === a.id ? '#fbbf24' : 'var(--text-secondary)',
                    fontSize: '11px', fontWeight: '500', cursor: 'pointer'
                  }}
                >
                  {a.label}
                </button>
              ))}
            </div>
          </div>

          {/* Season Phase */}
          <div style={{ marginBottom: '14px' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-secondary)', display: 'block', marginBottom: '6px' }}>Season Phase</label>
            <div style={{ display: 'flex', gap: '6px' }}>
              {[
                { id: 'off_season', label: 'Off-Season' },
                { id: 'pre_season', label: 'Pre-Season' },
                { id: 'in_season', label: 'In-Season' },
                { id: 'post_season', label: 'Post-Season' },
              ].map(p => (
                <button
                  key={p.id}
                  data-testid={`phase-${p.id}`}
                  onClick={() => setSelectedSeasonPhase(p.id)}
                  style={{
                    flex: 1,
                    padding: '8px 4px',
                    borderRadius: '8px',
                    border: selectedSeasonPhase === p.id ? '1px solid #fbbf24' : '1px solid rgba(255,255,255,0.1)',
                    background: selectedSeasonPhase === p.id ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.03)',
                    color: selectedSeasonPhase === p.id ? '#fbbf24' : 'var(--text-secondary)',
                    fontSize: '10px', fontWeight: '600', cursor: 'pointer', textAlign: 'center'
                  }}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <button
            data-testid="generate-sport-workout"
            onClick={() => generatePlan('sport_specific', null, null, null, null, null, selectedSport, selectedAgeGroup, selectedSeasonPhase)}
            disabled={loadingSport}
            style={{
              width: '100%', padding: '12px', borderRadius: '10px', border: 'none',
              background: loadingSport ? 'rgba(251,191,36,0.2)' : 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)',
              color: loadingSport ? '#fbbf24' : '#000',
              fontWeight: '700', fontSize: '13px', cursor: loadingSport ? 'wait' : 'pointer',
              marginBottom: '14px'
            }}
          >
            {loadingSport ? 'Generating Sport Program...' : 'Generate Sport-Specific Workout'}
          </button>

          {/* Generated Sport Workout Display */}
          {sportWorkout && (
            <div data-testid="sport-workout-result" style={{ 
              background: 'rgba(251,191,36,0.04)', borderRadius: '10px', padding: '14px',
              border: '1px solid rgba(251,191,36,0.12)'
            }}>
              <h4 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '700', color: '#fbbf24' }}>
                {sportWorkout.session_name || 'Sport Workout'}
              </h4>
              {sportWorkout.overview && (
                <p style={{ margin: '0 0 12px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                  {sportWorkout.overview}
                </p>
              )}
              {sportWorkout.sport_demands && (
                <div style={{ margin: '0 0 12px', padding: '8px 10px', background: 'rgba(251,191,36,0.06)', borderRadius: '8px', borderLeft: '3px solid #fbbf24' }}>
                  <span style={{ fontSize: '10px', fontWeight: '600', color: '#fbbf24', textTransform: 'uppercase' }}>Sport Demands</span>
                  <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--text-secondary)', lineHeight: '1.4' }}>{sportWorkout.sport_demands}</p>
                </div>
              )}

              {/* Warm-up */}
              {sportWorkout.warmup && (
                <div style={{ marginBottom: '12px' }}>
                  <h5 style={{ margin: '0 0 6px', fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    Warm-Up ({sportWorkout.warmup.duration_min || 8} min)
                  </h5>
                  {(sportWorkout.warmup.movements || []).map((m, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: '11px' }}>
                      <span style={{ color: 'var(--text-primary)' }}>{m.name}</span>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '10px' }}>{m.reps_or_duration}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Exercises */}
              {(sportWorkout.exercises || []).map((ex, i) => (
                <div key={i} style={{ 
                  marginBottom: '10px', padding: '10px', background: 'rgba(255,255,255,0.02)',
                  borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{i+1}. {ex.name}</span>
                    <span style={{ fontSize: '10px', color: '#fbbf24', fontWeight: '600' }}>{ex.sets} x {ex.reps}</span>
                  </div>
                  {ex.sport_relevance && (
                    <p style={{ margin: '2px 0', fontSize: '10px', color: '#fbbf24', fontStyle: 'italic' }}>
                      Sport transfer: {ex.sport_relevance}
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: '12px', fontSize: '10px', color: 'var(--text-secondary)', marginTop: '2px' }}>
                    <span>Rest: {ex.rest}</span>
                    {ex.notes && <span>{ex.notes}</span>}
                  </div>
                </div>
              ))}

              {/* Conditioning */}
              {sportWorkout.conditioning && (
                <div style={{ marginBottom: '12px', padding: '10px', background: 'rgba(251,191,36,0.06)', borderRadius: '8px' }}>
                  <h5 style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: '600', color: '#fbbf24' }}>
                    Conditioning: {sportWorkout.conditioning.type}
                  </h5>
                  <p style={{ margin: '0 0 4px', fontSize: '11px', color: 'var(--text-primary)' }}>{sportWorkout.conditioning.protocol}</p>
                  {sportWorkout.conditioning.notes && (
                    <p style={{ margin: '0', fontSize: '10px', color: 'var(--text-secondary)', fontStyle: 'italic' }}>{sportWorkout.conditioning.notes}</p>
                  )}
                </div>
              )}

              {/* Cooldown & Frequency */}
              <div style={{ display: 'flex', gap: '8px', marginBottom: '10px' }}>
                {sportWorkout.duration_min && (
                  <span style={{ padding: '4px 10px', background: 'rgba(251,191,36,0.1)', borderRadius: '12px', fontSize: '10px', fontWeight: '600', color: '#fbbf24' }}>
                    {sportWorkout.duration_min} min
                  </span>
                )}
                {sportWorkout.weekly_frequency && (
                  <span style={{ padding: '4px 10px', background: 'rgba(255,255,255,0.04)', borderRadius: '12px', fontSize: '10px', color: 'var(--text-secondary)' }}>
                    {sportWorkout.weekly_frequency}
                  </span>
                )}
              </div>

              {sportWorkout.cooldown && (
                <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  <strong>Cooldown:</strong> {sportWorkout.cooldown}
                </p>
              )}
              {sportWorkout.complement_sessions && (
                <p style={{ margin: '0 0 8px', fontSize: '11px', color: 'var(--text-secondary)' }}>
                  <strong>Complement with:</strong> {sportWorkout.complement_sessions}
                </p>
              )}

              {/* Notes */}
              {sportWorkout.notes && sportWorkout.notes.length > 0 && (
                <div style={{ padding: '8px 10px', background: 'rgba(255,255,255,0.02)', borderRadius: '6px', marginTop: '8px' }}>
                  {sportWorkout.notes.map((n, i) => (
                    <p key={i} style={{ margin: i === 0 ? '0' : '4px 0 0', fontSize: '10px', color: 'var(--text-secondary)' }}>
                      {n}
                    </p>
                  ))}
                </div>
              )}

              {/* Ask Coach */}
              {onAskCoach && (
                <button
                  data-testid="sport-ask-coach"
                  onClick={() => onAskCoach(`I just generated a ${sportWorkout.sport || selectedSport} workout for ${selectedSeasonPhase.replace('_', ' ')} phase. Can you help me understand the periodization and how to progress?`)}
                  style={{
                    marginTop: '10px', width: '100%', padding: '8px', borderRadius: '8px',
                    background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)',
                    color: '#fbbf24', fontSize: '11px', fontWeight: '600', cursor: 'pointer'
                  }}
                >
                  Ask Coach About This Program
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* HOME REHAB TAB */}
      {activeTab === 'home_rehab' && (
        <div data-testid="home-rehab-tab">
          {/* Subtitle */}
          <div style={{ marginBottom: '14px', padding: '10px 12px', background: 'rgba(34,197,94,0.04)',
            border: '1px solid rgba(34,197,94,0.1)', borderRadius: '8px' }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#22c55e', marginBottom: '3px' }}>
              Home Workouts
            </div>
            <div style={{ fontSize: '10px', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
              Strength, conditioning, and rehab workouts tailored to your home equipment. Select what you have and your focus area.
            </div>
          </div>

          {/* Focus Area Selection */}
          <div style={{ marginBottom: '12px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase',
              letterSpacing: '0.5px', marginBottom: '6px' }}>Focus Area</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {REHAB_FOCUSES.map(f => (
                <button
                  key={f.value}
                  data-testid={`rehab-focus-${f.value}`}
                  onClick={() => setRehabFocus(f.value)}
                  style={{
                    padding: '5px 10px', borderRadius: '6px', border: 'none', fontSize: '10px', fontWeight: '600',
                    cursor: 'pointer', transition: 'all 0.15s',
                    background: rehabFocus === f.value ? `${f.color}20` : 'rgba(255,255,255,0.04)',
                    color: rehabFocus === f.value ? f.color : 'var(--text-secondary)',
                    outline: rehabFocus === f.value ? `1px solid ${f.color}40` : 'none',
                  }}
                >{f.label}</button>
              ))}
            </div>
          </div>

          {/* Equipment Selection */}
          <div style={{ marginBottom: '14px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase',
              letterSpacing: '0.5px', marginBottom: '6px' }}>Available Equipment (select all you have)</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {EQUIPMENT_OPTIONS.map(eq => (
                <button
                  key={eq.value}
                  data-testid={`equipment-${eq.value.replace(/\s/g, '-')}`}
                  onClick={() => toggleEquipment(eq.value)}
                  style={{
                    padding: '5px 10px', borderRadius: '6px', border: 'none', fontSize: '10px', fontWeight: '600',
                    cursor: 'pointer', transition: 'all 0.15s',
                    background: selectedEquipment.includes(eq.value) ? 'rgba(34,197,94,0.12)' : 'rgba(255,255,255,0.04)',
                    color: selectedEquipment.includes(eq.value) ? '#22c55e' : 'var(--text-secondary)',
                    outline: selectedEquipment.includes(eq.value) ? '1px solid rgba(34,197,94,0.3)' : 'none',
                  }}
                >{eq.label}</button>
              ))}
            </div>
          </div>

          {/* Generate button */}
          <button
            data-testid="generate-home-rehab-btn"
            onClick={() => generatePlan('home_rehab', null, null, null, selectedEquipment, rehabFocus)}
            disabled={loadingHomeRehab}
            style={{
              width: '100%', padding: '12px', borderRadius: '8px', border: 'none',
              background: loadingHomeRehab ? 'rgba(34,197,94,0.3)' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: 'white', fontSize: '13px', fontWeight: '700',
              cursor: loadingHomeRehab ? 'not-allowed' : 'pointer', marginBottom: '14px',
              letterSpacing: '0.5px',
            }}
          >
            {loadingHomeRehab ? 'Generating Home Workout...' : `Generate ${REHAB_FOCUSES.find(f => f.value === rehabFocus)?.label || 'Home'} Workout`}
          </button>

          {/* Display generated workout */}
          {homeRehabWorkout && <HomeRehabView workout={homeRehabWorkout} />}
          {homeRehabWorkout && (
            <button data-testid="log-home-rehab-workout-btn" onClick={() => openLogForm('home_rehab', homeRehabWorkout)}
              style={{ width: '100%', padding: '10px', borderRadius: '8px', marginTop: '10px',
                border: '1px solid rgba(34,197,94,0.3)', background: 'rgba(34,197,94,0.06)',
                color: '#22c55e', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>
              Log This Workout
            </button>
          )}
        </div>
      )}

      {/* ═══ LOG WORKOUT FORM (shared across all tabs) ═══ */}
      {showLogForm && (
        <div data-testid="log-workout-form" style={{
          marginTop: '16px', padding: '14px', background: 'rgba(34,197,94,0.04)',
          border: '1px solid rgba(34,197,94,0.15)', borderRadius: '10px',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <div style={{ fontSize: '13px', fontWeight: '700', color: '#22c55e' }}>Log Workout</div>
            <button onClick={() => setShowLogForm(null)} style={{
              background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '18px', cursor: 'pointer'
            }}>&times;</button>
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginBottom: '10px' }}>{showLogForm.name}</div>

          {/* Duration */}
          <div style={{ marginBottom: '10px' }}>
            <label style={{ fontSize: '10px', fontWeight: '600', color: 'var(--text-muted)', textTransform: 'uppercase' }}>Duration (min)</label>
            <input data-testid="log-duration-input" type="number" value={logDuration} onChange={e => setLogDuration(parseInt(e.target.value) || 0)}
              style={{ width: '80px', marginLeft: '8px', padding: '4px 8px', borderRadius: '6px',
                border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                color: 'var(--text-primary)', fontSize: '12px' }} />
          </div>

          {/* Exercises */}
          <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase',
            letterSpacing: '0.5px', marginBottom: '6px' }}>Exercises</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
            {logExercises.map((ex, i) => (
              <div key={i} data-testid={`log-exercise-${i}`} style={{
                padding: '8px 10px', background: 'rgba(255,255,255,0.03)',
                border: `1px solid ${ex.completed ? 'rgba(34,197,94,0.2)' : 'rgba(255,255,255,0.06)'}`,
                borderRadius: '8px', opacity: ex.completed ? 1 : 0.5,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <input type="checkbox" checked={ex.completed} onChange={e => updateLogExercise(i, 'completed', e.target.checked)}
                    style={{ accentColor: '#22c55e' }} />
                  <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)', flex: 1 }}>{ex.name}</span>
                </div>
                {ex.completed && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginLeft: '24px' }}>
                    <input data-testid={`log-weight-${i}`} type="text" placeholder="Weight (e.g. 135lb)"
                      value={ex.weight || ''} onChange={e => updateLogExercise(i, 'weight', e.target.value)}
                      style={{ width: '100px', padding: '4px 6px', borderRadius: '5px', fontSize: '10px',
                        border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                        color: 'var(--text-primary)' }} />
                    <input data-testid={`log-reps-${i}`} type="text" placeholder={`Reps (${ex.reps || 'e.g. 10'})`}
                      value={typeof ex.reps === 'string' && ex.reps.includes('e.g') ? '' : ex.reps || ''} onChange={e => updateLogExercise(i, 'reps', e.target.value)}
                      style={{ width: '80px', padding: '4px 6px', borderRadius: '5px', fontSize: '10px',
                        border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                        color: 'var(--text-primary)' }} />
                    <input data-testid={`log-sets-${i}`} type="text" placeholder={`Sets (${ex.sets || '3'})`}
                      value={ex.sets || ''} onChange={e => updateLogExercise(i, 'sets', e.target.value)}
                      style={{ width: '60px', padding: '4px 6px', borderRadius: '5px', fontSize: '10px',
                        border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
                        color: 'var(--text-primary)' }} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Notes */}
          <textarea data-testid="log-notes-input" value={logNotes} onChange={e => setLogNotes(e.target.value)}
            placeholder="Overall notes (optional)" rows={2}
            style={{ width: '100%', padding: '8px', borderRadius: '6px', fontSize: '11px', resize: 'none',
              border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.04)',
              color: 'var(--text-primary)', marginBottom: '10px', boxSizing: 'border-box' }} />

          <button data-testid="submit-log-workout-btn" onClick={handleLogWorkout} disabled={loggingWorkout}
            style={{ width: '100%', padding: '11px', borderRadius: '8px', border: 'none',
              background: loggingWorkout ? 'rgba(34,197,94,0.3)' : 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
              color: 'white', fontSize: '13px', fontWeight: '700', cursor: loggingWorkout ? 'not-allowed' : 'pointer' }}>
            {loggingWorkout ? 'Logging...' : 'Log Workout'}
          </button>
        </div>
      )}

      {/* ═══ WORKOUT HISTORY ═══ */}
      {workoutLogs.length > 0 && (
        <div style={{ marginTop: '18px' }}>
          <button data-testid="toggle-workout-history" onClick={() => setShowHistory(!showHistory)}
            style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 10px', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)',
              background: 'rgba(255,255,255,0.03)', cursor: 'pointer', color: 'var(--text-secondary)' }}>
            <span style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Workout History ({workoutLogs.length})
            </span>
            <span style={{ fontSize: '14px', transition: 'transform 0.2s',
              transform: showHistory ? 'rotate(180deg)' : 'rotate(0deg)' }}>&#9662;</span>
          </button>
          {showHistory && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
              {workoutLogs.map(log => (
                <div key={log.log_id} data-testid={`workout-log-${log.log_id}`} style={{
                  padding: '10px 12px', background: 'rgba(255,255,255,0.03)',
                  border: '1px solid rgba(255,255,255,0.06)', borderRadius: '8px',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>{log.workout_name}</div>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '3px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '4px',
                          background: log.workout_type === 'crossfit' ? 'rgba(239,68,68,0.1)' : log.workout_type === 'home_rehab' ? 'rgba(34,197,94,0.1)' : 'rgba(102,126,234,0.1)',
                          color: log.workout_type === 'crossfit' ? '#ef4444' : log.workout_type === 'home_rehab' ? '#22c55e' : '#667eea',
                          fontWeight: '700', textTransform: 'uppercase' }}>
                          {log.workout_type === 'home_rehab' ? 'Rehab' : log.workout_type}
                        </span>
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{log.duration_minutes} min</span>
                        <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: '600' }}>{log.calories_burned} kcal</span>
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>
                          {log.completed_count}/{log.total_count} exercises
                        </span>
                      </div>
                    </div>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{log.date}</span>
                  </div>
                  {log.overall_notes && (
                    <p style={{ margin: '5px 0 0', fontSize: '10px', color: 'var(--text-muted)', fontStyle: 'italic' }}>{log.overall_notes}</p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkoutPlanner;
